import type { DietSlotFoodV1, Food, NutritionLog } from '@onflow/shared';

import { displayUnit } from './food-units.js';

// ADR-0015 natural-unit display. Two functions mirror each other; the only
// difference is field-name casing (NutritionLog is camelCase, DietSlotFoodV1
// is snake_case). Output strings are identical between the two surfaces so a
// food the user logs and the same food in their diet plan read the same way.
//
// Pluralization is intentionally skipped — "2 paratha" reads slightly off but
// is post-M6 polish (per ADR-0015).

/**
 * NutritionLog row → user-facing string.
 *
 *   count + size_label → "2 medium Roti / Chapati (70g)"
 *   count alone        → "3 boiled egg (150g)"
 *   portion_label      → "medium Dal tadka (yellow dal) (200g)"
 *   grams (or manual)  → "60g Paneer"
 *
 * `food` may be undefined when the catalog cache hasn't resolved yet — in
 * that case we fall back to the entry's foodId so the row still renders
 * (rare; the drawer always seeds the catalog query before rendering rows).
 */
export function formatNutritionLog(entry: NutritionLog, food: Food | undefined): string {
  const name = food?.name ?? entry.foodId;
  const grams = Math.round(entry.quantityG);
  const u = displayUnit(food); // 'ml' for drinks, 'g' otherwise
  if (entry.count != null) {
    const sizeStr = entry.sizeLabel ? `${entry.sizeLabel} ` : '';
    return `${entry.count} ${sizeStr}${name} (${grams}${u})`;
  }
  if (entry.portionLabel != null) {
    return `${entry.portionLabel} ${name} (${grams}${u})`;
  }
  return u === 'ml' ? `${grams}ml ${name}` : `${grams}g ${name}`;
}

/**
 * DietSlotFoodV1 row → user-facing string. Same shape and rules as
 * formatNutritionLog; falls back to the slot's denormalized food_name when
 * the catalog cache miss because slot rows always carry a food_name (the
 * catalog might not be loaded yet, but the persisted slot is self-contained).
 */
export function formatDietSlotFood(slotFood: DietSlotFoodV1, food: Food | undefined): string {
  const name = food?.name ?? slotFood.food_name;
  const grams = Math.round(slotFood.quantity_g);
  // Slot rows may resolve the catalog food (for the liquid check) or not; when
  // the food is absent we fall back to the denormalized name and grams.
  const u = displayUnit(food ?? { category: 'other', name });
  if (slotFood.count != null) {
    const sizeStr = slotFood.size_label ? `${slotFood.size_label} ` : '';
    return `${slotFood.count} ${sizeStr}${name} (${grams}${u})`;
  }
  if (slotFood.portion_label != null) {
    return `${slotFood.portion_label} ${name} (${grams}${u})`;
  }
  return u === 'ml' ? `${grams}ml ${name}` : `${grams}g ${name}`;
}
